import {
	StepAccordion,
	AccordionBackgroundColor,
	AccordionMinimumHeight,
	ButtonColorEnum,
} from "../../../../Utils/StepAccordion";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Dialog, DialogTitle, IconButton, Typography, styled } from "@mui/material";
import { DashedLine } from "../../../../Utils/DashedLine/DashedLine";
import IStepInterface from "../../../../../Interfaces/IStepInterface";
import "./ProductStep.scss";
import { useContext, useEffect, useMemo, useState } from "react";
import PartnerContentContext from "../../../../../Contexts/PartnerContentContext";
import { ProductCard } from "../../../../Utils/UI/ProductCard/ProductCard";
import { ProductCategory } from "../../../../../Models/Enums/ProductCategoryEnum";
import { IPartnerProduct } from "../../../../../Models/PartnerProductModel";
import { ProductService } from "../../../../../Services/PartnerProductService";
import { EmobilityStep } from "../EmobilityStep/EmobilityStep";
import { IPriceDetailsProps, PriceDetail } from "../../../../Utils/UI/PriceDetails/PriceDetail";
import { ITransportAndCharges } from "../../../../../Models/TransportAndCharges";
import { OrderModel } from "../../../../../Models/OrderModel";
import { useOrder } from "../../../../../Contexts/OrderContext";

export const hasResolvedSeparateDeliveryAddress = (order: OrderModel): boolean =>
	!order.separateDeliveryAddress ||
	Boolean(order.separateDeliveryAddressId && order.separateDeliveryAddressRequest);

const ProductStep = ({
	nextStep,
	previousStep,
	open,
	toggleOpen,
}: IStepInterface) => {
	const { order, updateOrder } = useOrder();
	const [chosenElCardKey, setChosenElCardKey] = useState("");
	const [chosenEmobCardKey, setChosenEmobCardKey] = useState("");
	const [electricityProducts, setElectricityProducts] = useState(Array<IPartnerProduct>());
	const [chargingStandProducts, setChargingStandProducts] = useState(Array<IPartnerProduct>());
	const [modalVisible, setModalVisible] = useState(false);
	const [loading, setIsLoading] = useState(false);
	const [priceDetailProps, setPriceDetailProps] = useState<IPriceDetailsProps>({
		price: 0,
		unit: "",
		renderInfoText: true,
		subcategory: "",
		category: "",
		discounts: {
			installationDiscount: false,
			subscriptionDiscount: false,
			substitutionDiscount: false,
		},
		emobilityInstallationDiscount: undefined,
		emobilitySubscriptionDiscount: undefined,
		emobilitySubstitutionDiscount: undefined,
		substitution: false,
	});
	const partnerContentContext = useContext(PartnerContentContext);

	const nextButtonActiveMemo = useMemo(() => {
		const needsElectricityAndCharger: boolean =
			partnerContentContext.isElectricityRequired && partnerContentContext.isChargingStandRequired;
		const needsElectricityOnly: boolean =
			partnerContentContext.isElectricityRequired && !partnerContentContext.isChargingStandRequired;
		const needsChargerOnly: boolean =
			!partnerContentContext.isElectricityRequired && partnerContentContext.isChargingStandRequired;
		let productSelectionValid: boolean;
		if (needsElectricityAndCharger) {
			productSelectionValid = (
				chosenElCardKey.length > 0 &&
				chosenEmobCardKey.length > 0 &&
				order.emobilityWishedInstallationMonthName.length > 0
			);
		} else if (needsElectricityOnly) {
			if (chosenEmobCardKey.length > 0)
				productSelectionValid = chosenElCardKey.length > 0 && order.emobilityWishedInstallationMonthName.length > 0;
			else productSelectionValid = chosenElCardKey.length > 0;
		} else if (needsChargerOnly) {
			productSelectionValid = chosenEmobCardKey.length > 0 && order.emobilityWishedInstallationMonthName.length > 0;
		} else {
			if (chosenEmobCardKey.length > 0)
				productSelectionValid = order.emobilityWishedInstallationMonthName.length > 0;
			else productSelectionValid = chosenElCardKey.length > 0;
		}

		return productSelectionValid && hasResolvedSeparateDeliveryAddress(order);
	}, [
		partnerContentContext,
		chosenEmobCardKey,
		chosenElCardKey,
		order.emobilityWishedInstallationMonthName,
		order.separateDeliveryAddress,
		order.separateDeliveryAddressId,
		order.separateDeliveryAddressRequest,
	]);

	useEffect(() => {
		setIsLoading(true);
		getProducts().then(() => setIsLoading(false));
	}, [order.deliveryAddressId]);

	useEffect(() => {
		const emobilitySelected = chosenEmobCardKey.length > 0;
		updateOrder((prev) => ({
			chosenProducts: {
				chargingstandProduct: chargingStandProducts.find((p) => p.id === chosenEmobCardKey),
				electricityProduct: electricityProducts.find((p) => p.id === chosenElCardKey),
			},
			emobilitySelected,
			emobilityWishedInstallationMonth: emobilitySelected
				? prev.emobilityWishedInstallationMonth
				: undefined,
			emobilityWishedInstallationMonthName: emobilitySelected
				? prev.emobilityWishedInstallationMonthName
				: "",
				separateDeliveryAddress: emobilitySelected ? prev.separateDeliveryAddress : false,
				separateDeliveryAddressId: emobilitySelected ? prev.separateDeliveryAddressId : "",
				separateDeliveryAddressString: emobilitySelected ? prev.separateDeliveryAddressString : "",
				separateDeliveryAddressRequest: emobilitySelected
					? prev.separateDeliveryAddressRequest
					: undefined,
		}));
	}, [chosenElCardKey, chosenEmobCardKey]);

	useEffect(() => {
		if (electricityProducts.length > 0) {
			getTransportAndCharges();
		}
	}, [electricityProducts]);

	async function getProducts() {
		let productIds = Array<any>();
		if (partnerContentContext.electricityProductIds) {
			partnerContentContext.electricityProductIds.forEach((id) => productIds.push(id.value));
		}
		if (partnerContentContext.chargingStandProductIds) {
			partnerContentContext.chargingStandProductIds.forEach((id) => productIds.push(id.value));
		}

		await ProductService.getProducts(productIds)
			.then((data) => {
				const { elProducts, emobProducts } = sortProductsByCategory(data);
				checkAutomaticSelection(elProducts, emobProducts);
			})
			.catch((error) => {
				console.error("Error fetching products:", error);
			});
	}

	async function getTransportAndCharges() {
		if (partnerContentContext.electricityProductIds.length > 0) {
			await ProductService.getTransportAndCharges(electricityProducts[0].crmProductId)
				.then((transportAndCharges: ITransportAndCharges) => {
					partnerContentContext.transportAndCharges = transportAndCharges;
				})
				.catch((error) => {
					console.error("Error fetching transport and charges:", error);
				});
		}
	}

	function checkAutomaticSelection(elProducts: Array<IPartnerProduct>, emobProducts: Array<IPartnerProduct>) {
		if (elProducts.length === 1) {
			if (partnerContentContext.isElectricityRequired || emobProducts.length === 0)
				setChosenElCardKey(elProducts[0].id);
		}
		if (emobProducts.length === 1) {
			if (partnerContentContext.isChargingStandRequired || elProducts.length === 0)
				setChosenEmobCardKey(emobProducts[0].id);
		}
	}

	function sortProductsByCategory(products: Array<IPartnerProduct>) {
		let elProducts = Array<IPartnerProduct>();
		let emobProducts = Array<IPartnerProduct>();

		if (products.length > 0) {
			products.forEach((product) => {
				if (product !== null) {
					product.category === ProductCategory.El
						? elProducts.push(product)
						: emobProducts.push(product);
				}
			});
		}

		setElectricityProducts(elProducts);
		setChargingStandProducts(emobProducts);
		return { elProducts, emobProducts };
	}

	function isEastInstallation(): boolean {
		return parseInt(order.addressRequest.postCode) < 5000;
	}

	function handlePriceDetailButtonClick(priceDetailProps: IPriceDetailsProps) {
		setPriceDetailProps(priceDetailProps);
		setModalVisible(true);
	}

	return (
		<StepAccordion
			loading={loading}
			loadingText={partnerContentContext.productStep.loading}
			loadingTitle={partnerContentContext.stepLoader.title}
			backgroundColor={AccordionBackgroundColor.GREY}
			label={partnerContentContext.productStep.title}
			open={open}
			click={toggleOpen}
			firstButtonLabel={
				partnerContentContext.productStep.backButton
					? partnerContentContext.productStep.backButton
					: "Tilbage"
			}
			firstButtonOutline={true}
			firstButton={previousStep}
			secondButtonLabel={
				partnerContentContext.productStep.nextButton
					? partnerContentContext.productStep.nextButton
					: "Næste"
			}
			secondButtonType={ButtonColorEnum.DARK_GREEN}
			secondButtonDisabled={!nextButtonActiveMemo}
			secondButton={nextStep}
			minimumHeight={AccordionMinimumHeight.SMALL}
			firstButtonId="productStepReturn"
			secondButtonId="productStepForward"
			secondButtonShadow={true}
		>
			<ProductsContainer>
				{electricityProducts.length > 0 && (
					<>
						<CategoryDivider>
							<Typography variant="h2">Elprodukt</Typography>
						</CategoryDivider>

						<ProductCategoryContainer>
							{electricityProducts.map((product) => (
								<ProductCard
									key={product.id}
									product={product}
									chosen={chosenElCardKey === product.id}
									useMainPrice={isEastInstallation()}
									setChosenOrderCardKey={setChosenElCardKey}
									onButtonClick={(priceDetailProps: IPriceDetailsProps) =>
										handlePriceDetailButtonClick(priceDetailProps)
									}
									discounts={{
										installationDiscount: false,
										subscriptionDiscount: false,
										substitutionDiscount: false,
									}}
									substitution={false}
								/>
							))}
						</ProductCategoryContainer>
					</>
				)}

				{chargingStandProducts.length > 0 && electricityProducts.length > 0 && (
					<DashedLine marginBottom="30" marginTop="30" />
				)}

				{chargingStandProducts.length > 0 && (
					<>
						<CategoryDivider>
							<Typography variant="h2">Ladeløsning</Typography>
						</CategoryDivider>

						<ProductCategoryContainer>
							{chargingStandProducts.map((product) => (
								<ProductCard
									key={product.id}
									product={product}
									chosen={chosenEmobCardKey === product.id}
									useMainPrice={product.secondaryPrice === 0}
									setChosenOrderCardKey={setChosenEmobCardKey}
									onButtonClick={(priceDetailProps: IPriceDetailsProps) =>
										handlePriceDetailButtonClick(priceDetailProps)
									}
									discounts={{
										installationDiscount: partnerContentContext.emobilityInstallationDiscountActive,
										subscriptionDiscount: partnerContentContext.emobilitySubscriptionDiscountActive,
										substitutionDiscount: partnerContentContext.emobilitySubstitutionDiscountActive,
									}}
									substitution={order.emobilityRemovalOfChargingStand ?? false}
								/>
							))}
						</ProductCategoryContainer>

						{order.emobilitySelected && <EmobilityStep />}
					</>
				)}
			</ProductsContainer>

			<Dialog open={modalVisible} onClose={() => setModalVisible(false)} maxWidth="md" fullWidth>
				<DialogTitle sx={{ m: 0, p: 1 }}>
					<IconButton
						onClick={() => setModalVisible(false)}
						size="small"
						sx={{ position: "absolute", right: 8, top: 8 }}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>
				
				<PriceDetail
					price={priceDetailProps.price}
					unit={priceDetailProps?.unit}
					renderInfoText={priceDetailProps.renderInfoText}
					subcategory={priceDetailProps.subcategory}
					category={priceDetailProps.category}
					discounts={priceDetailProps.discounts}
					emobilityInstallationDiscount={priceDetailProps.emobilityInstallationDiscount}
					emobilitySubscriptionDiscount={priceDetailProps.emobilitySubscriptionDiscount}
					emobilitySubstitutionDiscount={priceDetailProps.emobilitySubstitutionDiscount}
					substitution={priceDetailProps.substitution}
				/>
			</Dialog>
		</StepAccordion>
	);
};

export default ProductStep;

const ProductsContainer = styled(Box)`
	display: flex;
	flex-direction: column;
	margin: 0px -120px 0px;

	@media (max-width: 768px) {
		margin: 0 0 5rem 0;
	}
`;

const ProductCategoryContainer = styled(Box)`
	display: flex;
	flex-direction: row;
	align-items: end;
	justify-content: space-evenly;
	margin-bottom: 1rem;

	@media (max-width: 768px) {
		flex-direction: column;
		align-items: center;
		margin-bottom: 2rem;
	}
`;

const CategoryDivider = styled(Box)`
	margin: 0 2rem 1rem;

	@media (max-width: 768px) {
		margin: 0.5rem 2rem 0;
	}
`;
